import React from 'react';

/**
 * Weighted I³ Score gauge — a hand-built SVG semicircle on the 0–250 burden
 * scale. Green / yellow / orange / red zones match the bar palette; the needle
 * points at the weighted score; the band thresholds (48 / 86 / 120) are number
 * ticks; and the LOW / MILD / MODERATE / HIGH scale sits in a labeled strip
 * below the dial with the active band highlighted. Pure SVG (no Highcharts) so
 * it scales to the panel width and renders in tests.
 */
type Props = {
  value: number;
  scoreText: string; // e.g. "63.5 / 250"
  bandLabel: string; // one of LOW / MILD / MODERATE / HIGH
  bandColor: string; // legible dark shade of the active band
};

const MAX = 250;
const VIEW_W = 260;
const VIEW_H = 140;
const CX = 130;
const CY = 120;
// Thick band to match the app's other gauges (~60% inner / 100% outer radius).
// R is the arc centerline; outer edge = R + STROKE/2 = 96, inner = 58 (≈60%).
const R = 77;
const STROKE = 38;

const GREEN = '#78F880';
const YELLOW = '#FEFA83';
const ORANGE = '#FFD37E';
const RED = '#FC6552';

const SEGMENTS = [
  { color: GREEN, from: 0, to: 48 },
  { color: YELLOW, from: 48, to: 86 },
  { color: ORANGE, from: 86, to: 120 },
  { color: RED, from: 120, to: MAX },
];
const TICKS = [0, 48, 86, 120, MAX];
const BANDS = [
  { label: 'LOW', color: GREEN, fg: '#14521d' },
  { label: 'MILD', color: YELLOW, fg: '#2e2a00' },
  { label: 'MODERATE', color: ORANGE, fg: '#3d2700' },
  { label: 'HIGH', color: RED, fg: '#5a1109' },
];

// value → angle in degrees (0 → 180° left, MAX → 0° right).
const angleFor = (v: number): number => 180 - (Math.max(0, Math.min(v, MAX)) / MAX) * 180;
// polar point with y pointing up (SVG y is down, hence CY - ...).
const polar = (r: number, deg: number) => {
  const a = (deg * Math.PI) / 180;
  return { x: CX + r * Math.cos(a), y: CY - r * Math.sin(a) };
};
const arcPath = (r: number, vFrom: number, vTo: number): string => {
  const a0 = angleFor(vFrom);
  const a1 = angleFor(vTo);
  const p0 = polar(r, a0);
  const p1 = polar(r, a1);
  const largeArc = Math.abs(a0 - a1) > 180 ? 1 : 0;
  return `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
};

export class ImmuneInflammatoryBurdenGauge extends React.Component<Props> {
  render() {
    const { value, scoreText, bandLabel, bandColor } = this.props;
    // Needle reaches near the outer edge of the (now thicker) band.
    const tip = polar(R + STROKE / 2 - 6, angleFor(value));

    return (
      <div style={{ width: '100%', textAlign: 'center' }}>
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          style={{ width: '100%', maxWidth: '400px', height: 'auto', display: 'block', margin: '0 auto' }}
        >
          {/* colored band arcs */}
          {SEGMENTS.map(s => (
            <path
              key={s.from}
              d={arcPath(R, s.from, s.to)}
              stroke={s.color}
              strokeWidth={STROKE}
              fill="none"
            />
          ))}
          {/* threshold tick marks + numbers */}
          {TICKS.map(t => {
            const a = angleFor(t);
            const i = polar(R + STROKE / 2 + 1, a);
            const o = polar(R + STROKE / 2 + 7, a);
            const l = polar(R + STROKE / 2 + 14, a);
            const anchor = a >= 100 ? 'end' : a <= 80 ? 'start' : 'middle';
            return (
              <g key={t}>
                <line x1={i.x} y1={i.y} x2={o.x} y2={o.y} stroke="#aaa" strokeWidth={1} />
                <text x={l.x} y={l.y} textAnchor={anchor} dominantBaseline="central" fontSize={9} fill="#777">
                  {t}
                </text>
              </g>
            );
          })}
          {/* needle */}
          <line x1={CX} y1={CY} x2={tip.x} y2={tip.y} stroke="#333" strokeWidth={3} strokeLinecap="round" />
          <circle cx={CX} cy={CY} r={6} fill="#333" />
        </svg>

        <div style={{ marginTop: '2px' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>{scoreText}</div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: bandColor }}>{bandLabel} Burden Level</div>
        </div>

        {/* LOW / MILD / MODERATE / HIGH scale, active band highlighted */}
        <div style={{ display: 'flex', gap: '3px', maxWidth: '340px', margin: '8px auto 0' }}>
          {BANDS.map(b => {
            const active = b.label === bandLabel;
            return (
              <div
                key={b.label}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '3px 2px',
                  borderRadius: '4px',
                  background: active ? b.color : '#f1f3f5',
                  color: active ? b.fg : '#adb5bd',
                  fontWeight: active ? 700 : 600,
                  fontSize: '10.5px',
                  letterSpacing: '0.2px',
                }}
              >
                {b.label}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

export default ImmuneInflammatoryBurdenGauge;
